import { useMutation } from "@tanstack/react-query";
import { doctorLoginUseCase, googleLoginUseCase, loginUseCase } from "../../application/use-cases/login.use-case";
import { cancelAccountDeletionWithPasswordUseCase } from "../../application/use-cases/cancel-account-deletion.use-case";
import { forgotPasswordUseCase } from "../../application/use-cases/forgot-password.use-case";
import { initiateRegistrationUseCase } from "../../application/use-cases/initiate-registration.use-case";
import { logoutUseCase } from "../../application/use-cases/logout.use-case";
import { onboardingUseCase } from "../../application/use-cases/onboarding.use-case";
import { resetPasswordUseCase } from "../../application/use-cases/reset-password.use-case";
import { setPasswordUseCase } from "../../application/use-cases/set-password.use-case";
import { verifyEmailUseCase } from "../../application/use-cases/verify-email.use-case";
import type {
  InitiateRegistrationInput,
  LoginCredentials,
  OnboardingInput,
} from "../../application/ports/auth.repository.port";
import { authHttpRepository } from "../http/auth.http-repository";

export function useLoginMutation() {
  return useMutation({
    mutationFn: (credentials: LoginCredentials) => loginUseCase(authHttpRepository, credentials),
  });
}

export function useCancelAccountDeletionWithPasswordMutation() {
  return useMutation({
    mutationFn: (credentials: LoginCredentials) =>
      cancelAccountDeletionWithPasswordUseCase(authHttpRepository, credentials),
  });
}

export function useGoogleLoginMutation() {
  return useMutation({
    mutationFn: (idToken: string) => googleLoginUseCase(authHttpRepository, idToken),
  });
}

export function useDoctorLoginMutation() {
  return useMutation({
    mutationFn: (credentials: LoginCredentials) => doctorLoginUseCase(authHttpRepository, credentials),
  });
}

export function useOnboardingMutation() {
  return useMutation({
    mutationFn: (input: OnboardingInput) => onboardingUseCase(authHttpRepository, input),
  });
}

export function useLogoutMutation() {
  return useMutation({
    mutationFn: (refreshToken: string) => logoutUseCase(authHttpRepository, refreshToken),
  });
}

export function useInitiateRegistrationMutation() {
  return useMutation({
    mutationFn: (input: InitiateRegistrationInput) => initiateRegistrationUseCase(authHttpRepository, input),
  });
}

export function useVerifyEmailMutation() {
  return useMutation({
    mutationFn: (token: string) => verifyEmailUseCase(authHttpRepository, token),
  });
}

export function useSetPasswordMutation() {
  return useMutation({
    mutationFn: (password: string) => setPasswordUseCase(authHttpRepository, password),
  });
}

export function useForgotPasswordMutation() {
  return useMutation({
    mutationFn: (email: string) => forgotPasswordUseCase(authHttpRepository, email),
  });
}

export function useResetPasswordMutation() {
  return useMutation({
    mutationFn: ({ token, newPassword }: { token: string; newPassword: string }) =>
      resetPasswordUseCase(authHttpRepository, token, newPassword),
  });
}
